import { Suspense, useEffect, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Grid } from "@react-three/drei";
import * as THREE from "three";

const CONCRETE = "#aab4c4";
const BRICK = "#a8593c";
const PLASTER = "#e9e4d8";
const FLOOR = "#cfc4ae";
const STEEL_ORANGE = "#ff6a3d";
const WATER_BLUE = "#4cc3ff";

const easeOut = (t) => 1 - Math.pow(1 - t, 3);

function useSyncedRef(value) {
    const ref = useRef(value);
    useEffect(() => { ref.current = value; }, [value]);
    return ref;
}

function Stage({ index, progressRef, explodedRef, selectedRef, onSelect, children }) {
    const group = useRef();
    const explodedTarget = useRef(0);

    useFrame(() => {
        if (!group.current) return;
        const p = progressRef.current;
        const ex = explodedRef.current;
        explodedTarget.current = THREE.MathUtils.lerp(explodedTarget.current, ex, 0.09);
        const e = explodedTarget.current;

        const local = easeOut(THREE.MathUtils.clamp(p * 9 - index, 0, 1));
        const visible = e > 0.03 ? 1 : local;
        group.current.visible = visible > 0.02;

        const buildY = (1 - local) * -2.4;
        const explodeY = e * index * 1.35;
        group.current.position.y = buildY + explodeY;

        const sel = selectedRef ? selectedRef.current : -1;
        const dimmed = e > 0.5 && sel !== -1 && sel !== index;
        const opacity = Math.max(visible, e) * (dimmed ? 0.12 : 1);

        group.current.traverse((o) => {
            if (o.material) {
                o.material.transparent = true;
                o.material.opacity = opacity * (o.userData.baseOpacity ?? 1);
                if (o.material.emissive && sel === index && e > 0.5) {
                    o.material.emissiveIntensity = 0.55 + Math.sin(performance.now() / 300) * 0.2;
                }
            }
        });
    });

    return (
        <group
            ref={group}
            onClick={onSelect ? (ev) => { ev.stopPropagation(); onSelect(index); } : undefined}
            onPointerOver={onSelect ? () => (document.body.style.cursor = "pointer") : undefined}
            onPointerOut={onSelect ? () => (document.body.style.cursor = "auto") : undefined}
        >
            {children}
        </group>
    );
}

const box = (key, size, pos, color, opts = {}) => (
    <mesh key={key} position={pos} userData={{ baseOpacity: opts.opacity ?? 1 }}>
        <boxGeometry args={size} />
        <meshStandardMaterial color={color} roughness={opts.roughness ?? 0.75} metalness={opts.metalness ?? 0.05}
            emissive={opts.emissive ?? "#000000"} emissiveIntensity={opts.emissiveIntensity ?? 0} />
    </mesh>
);

function BuildingModel({ progressRef, explodedRef, selectedRef, onSelect }) {
    const stageProps = { progressRef, explodedRef, selectedRef, onSelect };

    const columns = useMemo(() => {
        const cols = [];
        [-2.9, 0, 2.9].forEach((x) => [-2.1, 2.1].forEach((z) => cols.push([x, z])));
        return cols;
    }, []);

    const walls = useMemo(() => {
        const list = [];
        [0, 1].forEach((f) => {
            const y = 0.75 + f * 2.35 + 1.05;
            list.push({ size: [6.2, 2.1, 0.18], pos: [0, y, 2.26] });
            list.push({ size: [6.2, 2.1, 0.18], pos: [0, y, -2.26] });
            list.push({ size: [0.18, 2.1, 4.35], pos: [3.01, y, 0] });
            list.push({ size: [0.18, 2.1, 4.35], pos: [-3.01, y, 0] });
        });
        return list;
    }, []);

    return (
        <group position={[0, -1.6, 0]}>
            {/* 0 SITE */}
            <Stage index={0} {...stageProps}>
                {box("plot", [7.2, 0.18, 5.6], [0, 0.09, 0], "#12325e", { roughness: 0.9 })}
                {box("plot-edge", [7.4, 0.06, 5.8], [0, 0.03, 0], "#1E50A2")}
            </Stage>

            {/* 1 FOUNDATION */}
            <Stage index={1} {...stageProps}>
                {box("raft", [6.4, 0.5, 4.8], [0, 0.43, 0], CONCRETE)}
                {columns.map(([x, z], i) => box(`foot-${i}`, [0.95, 0.45, 0.95], [x, 0.2, z], "#8792a3"))}
            </Stage>

            {/* 2 STRUCTURE */}
            <Stage index={2} {...stageProps}>
                {columns.map(([x, z], i) => box(`col-${i}`, [0.26, 4.9, 0.26], [x, 3.13, z], "#c6cedb", { roughness: 0.6 }))}
                {[1, 2].map((f) => {
                    const y = 0.68 + f * 2.35;
                    return [
                        box(`slab-${f}`, [6.4, 0.16, 4.8], [0, y, 0], CONCRETE),
                        box(`beamXf-${f}`, [6.4, 0.22, 0.22], [0, y - 0.19, 2.1], "#b7c1d1"),
                        box(`beamXb-${f}`, [6.4, 0.22, 0.22], [0, y - 0.19, -2.1], "#b7c1d1"),
                        box(`beamZl-${f}`, [0.22, 0.22, 4.8], [-2.9, y - 0.19, 0], "#b7c1d1"),
                        box(`beamZr-${f}`, [0.22, 0.22, 4.8], [2.9, y - 0.19, 0], "#b7c1d1"),
                    ];
                })}
            </Stage>

            {/* 3 MASONRY */}
            <Stage index={3} {...stageProps}>
                {walls.map((w, i) => box(`wall-${i}`, w.size, w.pos, BRICK, { opacity: 0.94, roughness: 0.95 }))}
            </Stage>

            {/* 4 ELECTRICAL */}
            <Stage index={4} {...stageProps}>
                {[-2.2, 0, 2.2].map((x, i) =>
                    box(`el-v-${i}`, [0.06, 4.4, 0.06], [x, 3.1, 2.12], STEEL_ORANGE, { emissive: STEEL_ORANGE, emissiveIntensity: 0.35 }))}
                {[1.6, 3.95].map((y, i) =>
                    box(`el-h-${i}`, [5.6, 0.06, 0.06], [0, y, 2.12], STEEL_ORANGE, { emissive: STEEL_ORANGE, emissiveIntensity: 0.35 }))}
            </Stage>

            {/* 5 PLUMBING */}
            <Stage index={5} {...stageProps}>
                {[-2.6, 2.6].map((x, i) =>
                    box(`pl-v-${i}`, [0.07, 4.6, 0.07], [x, 3.0, -2.14], WATER_BLUE, { emissive: WATER_BLUE, emissiveIntensity: 0.3 }))}
                {[1.2, 3.55].map((y, i) =>
                    box(`pl-h-${i}`, [0.07, 0.07, 4.2], [-2.6 + i * 5.2, y, 0], WATER_BLUE, { emissive: WATER_BLUE, emissiveIntensity: 0.3 }))}
            </Stage>

            {/* 6 FLOORING */}
            <Stage index={6} {...stageProps}>
                {[3.11, 5.46].map((y, i) =>
                    box(`floor-${i}`, [6.1, 0.07, 4.5], [0, y, 0], FLOOR, { roughness: 0.25, metalness: 0.15 }))}
            </Stage>

            {/* 7 FINISHING */}
            <Stage index={7} {...stageProps}>
                {walls.map((w, i) => box(`plaster-${i}`, [w.size[0] + (w.size[0] < 1 ? 0.06 : 0), w.size[1] + 0.04, w.size[2] + (w.size[2] < 1 ? 0.06 : 0)], w.pos, PLASTER, { opacity: 0.55, roughness: 0.85 }))}
                {[-1.8, 0, 1.8].map((x, i) =>
                    box(`win-${i}`, [1.1, 1.0, 0.1], [x, 2.0, 2.32], "#ffd9a0", { emissive: "#ffb35c", emissiveIntensity: 0.9, roughness: 0.2 }))}
                {[-1.8, 1.8].map((x, i) =>
                    box(`win2-${i}`, [1.1, 1.0, 0.1], [x, 4.35, 2.32], "#ffd9a0", { emissive: "#ffb35c", emissiveIntensity: 0.9, roughness: 0.2 }))}
                {box("door", [0.95, 1.9, 0.1], [0, 1.65, 2.33], "#3b2a1d", { roughness: 0.5 })}
            </Stage>

            {/* 8 ROOF / FINAL */}
            <Stage index={8} {...stageProps}>
                {box("roof", [6.6, 0.22, 5.0], [0, 5.75, 0], "#d7dee9", { roughness: 0.5 })}
                {box("parapet-f", [6.6, 0.5, 0.14], [0, 6.1, 2.43], PLASTER)}
                {box("parapet-b", [6.6, 0.5, 0.14], [0, 6.1, -2.43], PLASTER)}
                {box("parapet-l", [0.14, 0.5, 5.0], [-3.23, 6.1, 0], PLASTER)}
                {box("parapet-r", [0.14, 0.5, 5.0], [3.23, 6.1, 0], PLASTER)}
                {box("tank", [1.0, 0.9, 1.0], [-2.2, 6.35, -1.2], "#1E50A2", { roughness: 0.4 })}
                <pointLight position={[0, 3.2, 0]} intensity={explodedRef.current > 0.5 ? 0 : 14} color="#ffbe78" distance={9} />
            </Stage>
        </group>
    );
}

function CameraRig({ progressRef, enabled }) {
    const vec = useMemo(() => new THREE.Vector3(), []);
    useFrame(({ camera }) => {
        if (!enabled) return;
        const p = progressRef.current;
        const angle = 0.75 + p * 1.15;
        const radius = 14.5 - p * 3.2;
        vec.set(Math.sin(angle) * radius, 6.2 + p * 2.6, Math.cos(angle) * radius);
        camera.position.lerp(vec, 0.07);
        camera.lookAt(0, 1.6, 0);
    });
    return null;
}

export const BuildingCanvas = ({ progressRef, exploded = false, selected = -1, onSelect, lite = false, className }) => {
    const explodedRef = useSyncedRef(exploded ? 1 : 0);
    const selectedRef = useSyncedRef(selected);
    const internalProgress = useRef(1);
    const activeProgress = progressRef || internalProgress;

    return (
        <Canvas
            className={className}
            dpr={lite ? [1, 1.2] : [1, 1.75]}
            camera={exploded ? { position: [18, 11, 19], fov: 40 } : { position: [11, 7, 12], fov: 38 }}
            gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
            data-testid="building-3d-canvas"
        >
            <Suspense fallback={null}>
                <ambientLight intensity={0.85} />
                <directionalLight position={[7, 12, 5]} intensity={1.6} color="#ffffff" />
                <directionalLight position={[-8, 6, -6]} intensity={0.5} color="#7ea8ff" />
                <spotLight position={[0, 14, 8]} angle={0.5} penumbra={0.8} intensity={60} color="#ff8a50" />
                <Grid
                    position={[0, -1.62, 0]}
                    args={[40, 40]}
                    cellSize={1.2}
                    cellColor="#1c3a63"
                    sectionSize={6}
                    sectionColor="#2c497f"
                    fadeDistance={34}
                    fadeStrength={2.2}
                    infiniteGrid
                />
                <BuildingModel progressRef={activeProgress} explodedRef={explodedRef} selectedRef={selectedRef} onSelect={onSelect} />
                <CameraRig progressRef={activeProgress} enabled={!exploded} />
                {exploded && <OrbitControls autoRotate autoRotateSpeed={0.9} enableZoom={false} enablePan={false}
                    target={[0, 5.4, 0]}
                    minPolarAngle={Math.PI / 3.2} maxPolarAngle={Math.PI / 1.9} />}
            </Suspense>
        </Canvas>
    );
};
